import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { adminService } from '../../services/loanService';
import { LoanStatusBadge } from '../../components/common/StatusBadge';
import { formatCurrency, formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../hooks/useAuth';

const FILTERS = ['', 'submitted', 'under_review', 'credit_check', 'approved', 'offer_sent', 'offer_accepted', 'disbursed', 'active', 'rejected'];
const REVIEWABLE = ['submitted', 'under_review', 'credit_check'];

export default function ApplicationsList() {
  const { isMaker, isChecker } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-loans', filter],
    queryFn: () => adminService.getAllLoans(filter ? { status: filter } : {}).then(r => r.data.data),
  });

  const loans = data?.results || data || [];

  return (
    <div>
      <div className="page-header">
        <h1>Loan Applications</h1>
        <p>{(isMaker || isChecker) ? 'Review and action loan applications — approve/reject creates a checker approval request' : 'View loan applications across all statuses'}</p>
      </div>

      {!isMaker && !isChecker && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '0.75rem 1rem', marginBottom: '1rem',
          background: 'rgba(91,142,255,0.07)', border: '1px solid rgba(91,142,255,0.25)',
          borderRadius: 'var(--radius)', fontSize: '0.82rem', color: 'var(--text-2)',
        }}>
          <span style={{ fontSize: '1rem' }}>👁</span>
          <span>You are in <strong>read-only</strong> mode. Only users with the <strong>Maker</strong> or <strong>Checker</strong> role can review or action loan applications.</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {FILTERS.map(s => (
          <button key={s}
            className={`btn ${filter === s ? 'btn-primary' : 'btn-ghost'}`}
            style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}
            onClick={() => setFilter(s)}>
            {s ? s.replace(/_/g, ' ') : 'All'}
          </button>
        ))}
      </div>

      <div className="card" style={{ padding: 0 }}>
        {isLoading ? <div style={{ padding: '2rem' }}><LoadingSpinner /></div> : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>App #</th><th>Applicant</th><th>Type</th><th>Amount</th><th>Tenure</th><th>KYC</th><th>Status</th><th>Submitted</th><th></th></tr>
              </thead>
              <tbody>
                {loans.length === 0 ? (
                  <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '2rem' }}>No applications found.</td></tr>
                ) : loans.map(loan => (
                  <tr key={loan.id}>
                    <td style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem' }}>{loan.application_number}</td>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{loan.applicant_name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{loan.applicant_email}</div>
                      <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase', marginTop: 2 }}>
                        {loan.applicant_tier} tier
                      </div>
                    </td>
                    <td style={{ textTransform: 'capitalize', fontSize: '0.875rem' }}>{loan.loan_type}</td>
                    <td style={{ fontWeight: 600 }}>{formatCurrency(loan.approved_amount ?? loan.requested_amount)}</td>
                    <td style={{ fontSize: '0.875rem' }}>{loan.requested_tenure_months}mo</td>
                    <td>
                      <span style={{
                        fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.5rem', borderRadius: 99, textTransform: 'uppercase',
                        background: loan.kyc_status === 'approved' ? 'rgba(0,200,150,0.12)' : 'rgba(255,71,87,0.12)',
                        color: loan.kyc_status === 'approved' ? '#00C896' : '#FF4757',
                      }}>
                        {loan.kyc_status}
                      </span>
                    </td>
                    <td><LoanStatusBadge status={loan.status} /></td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{formatDate(loan.submitted_at || loan.created_at)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'nowrap' }}>
                        {REVIEWABLE.includes(loan.status) && ((isMaker && !loan.maker_recommended_at) || isChecker) ? (
                          <button
                            className="btn btn-primary"
                            style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem', whiteSpace: 'nowrap' }}
                            onClick={() => navigate(`/admin/applications/${loan.id}`)}>
                            Review
                          </button>
                        ) : (
                          <Link
                            to={`/admin/applications/${loan.id}`}
                            className="btn btn-ghost"
                            style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}>
                            View
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
